import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";

const API_BASE = "https://3d8d71qhr3.execute-api.ap-south-1.amazonaws.com/Prod";

const safe = (v, fallback = "-") =>
  v === null || v === undefined || v === "" ? fallback : String(v);

const normalizeNode = (n) => {
  const children = Array.isArray(n?.children)
    ? n.children.map(normalizeNode).filter(Boolean)
    : [];
  return {
    userId: n?.userId || "",
    name: n?.name || "",   // ← add this line
    level: n?.level ?? "",
    children,
  };
};

const countNodes = (node) =>
  1 + (node.children || []).reduce((acc, c) => acc + countNodes(c), 0);

function ReferralNode({ node, depth = 0 }) {
  const [open, setOpen] = useState(depth < 1);
  const hasChildren = (node.children || []).length > 0;

  return (
    <div className="mb-2">
      <div
        className="d-flex align-items-start gap-2 p-2 rounded-3 border"
        style={{ marginLeft: depth * 16 }}
      >
        <div style={{ width: 26 }}>
          {hasChildren ? (
            <button
              className="btn btn-sm btn-outline-secondary rounded-circle"
              style={{ width: 26, height: 26, padding: 0 }}
              onClick={() => setOpen((v) => !v)}
              title={open ? "Collapse" : "Expand"}
            >
              {open ? "−" : "+"}
            </button>
          ) : (
            <span className="text-muted">•</span>
          )}
        </div>

        <div className="flex-grow-1">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <span className="fw-semibold text-dark">
              {safe(node.userId)} &mdash; {safe(node.name)}{" "}
              <span className="text-muted small">
                [{node.level === 0 ? "Master" : `Level ${safe(node.level)}`}]
              </span>
            </span>
            <span className="badge bg-light text-dark border">
              Children: {node.children?.length || 0}
            </span>
          </div>
          <div className="text-muted small mt-1">
            Downline count: {countNodes(node) - 1}
          </div>
        </div>
      </div>

      {open && hasChildren ? (
        <div className="mt-2">
          {node.children.map((c, i) => (
            <ReferralNode
              key={`${c.userId || i}_${i}`}
              node={c}
              depth={depth + 1}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AdminReferralTree() {
  const [roots, setRoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  /* filter — separate input from applied to fix clear bug */
  const [userIdInput, setUserIdInput] = useState("");
  const [appliedUserId, setAppliedUserId] = useState("");

  const adminToken = localStorage.getItem("adminToken");

  const fetchTree = async (userId = appliedUserId) => {
    setLoading(true);
    setErrorMsg("");

    if (!adminToken) {
      setErrorMsg("Admin session missing. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const qs = new URLSearchParams();
      if (userId.trim()) qs.set("userId", userId.trim());

      const url = qs.toString().length
        ? `${API_BASE}/RefferalTree/GetRefferalTree?${qs.toString()}`
        : `${API_BASE}/RefferalTree/GetRefferalTree`;

      const res = await fetch(url, { headers: { Authorization: adminToken } });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Request failed (${res.status})`);
      }

      const result = await res.json();
      const normalizedRoots = Array.isArray(result)
        ? result.map(normalizeNode).filter(Boolean)
        : [];

      setRoots(normalizedRoots);
    } catch (err) {
      setErrorMsg(err?.message || "Failed to fetch referral tree.");
      setRoots([]);
    } finally {
      setLoading(false);
    }
  };

  /* initial load */
  useEffect(() => {
    fetchTree("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    setAppliedUserId(userIdInput);
    fetchTree(userIdInput);
  };

  const handleClear = () => {
    setUserIdInput("");
    setAppliedUserId("");
    fetchTree("");
  };

  const totalNodes = useMemo(
    () => roots.reduce((acc, r) => acc + countNodes(r), 0),
    [roots],
  );

  return (
    <>
      <Header />

      <main className="pb-4">
        <section className="py-4">
          <div className="container d-flex justify-content-center">
            <div className="col-12">
              {/* Header row */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h3 className="m-0 fw-semibold lh-1">
                    Referral Tree ({totalNodes})
                  </h3>
                  <div className="text-muted">
                    View Full Tree / Expand & Collapse Downlines
                  </div>
                </div>

                <div
                  className="icon-shape icon-md bg-light-primary rounded-circle cursor-pointer"
                  onClick={() => fetchTree(appliedUserId)}
                  title="Refresh"
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm text-dark" />
                  ) : (
                    <i className="bi bi-arrow-clockwise fs-3 text-primary" />
                  )}
                </div>
              </div>

              {/* ── Professional Filter Card ── */}
              <div
                className="card border-0 shadow-sm rounded-4 mb-3"
                style={{
                  background:
                    "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                }}
              >
                <div className="card-body px-4 py-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md">
                      <label
                        className="form-label fw-medium mb-1"
                        style={{ fontSize: 13 }}
                      >
                        <i className="bi bi-person me-1 text-muted" />
                        User ID
                      </label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i
                            className="bi bi-search text-muted"
                            style={{ fontSize: 13 }}
                          />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          value={userIdInput}
                          onChange={(e) => setUserIdInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleApply()}
                          placeholder="Search by User ID…"
                          disabled={loading}
                          style={{ fontSize: 14 }}
                        />
                      </div>
                    </div>

                    <div className="col-12 col-md-auto d-flex gap-2">
                      <button
                        className="btn btn-primary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={loading}
                        onClick={handleApply}
                      >
                        <i className="bi bi-check2 me-1" />
                        Apply
                      </button>
                      <button
                        className="btn btn-outline-secondary px-4 fw-medium"
                        style={{ borderRadius: 10, fontSize: 14 }}
                        disabled={loading || (!userIdInput && !appliedUserId)}
                        onClick={handleClear}
                      >
                        <i className="bi bi-x-lg me-1" />
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {errorMsg ? (
                <div className="alert alert-danger rounded-4">{errorMsg}</div>
              ) : null}

              {/* Tree */}
              <div className="card shadow-sm rounded-4">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <span className="spinner-border spinner-border-sm me-2" />
                      Loading tree...
                    </div>
                  ) : roots.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      No tree data found.
                    </div>
                  ) : (
                    roots.map((r, idx) => (
                      <div key={r.userId || idx} className="mb-3">
                        <div className="fw-semibold mb-2 text-muted">
                          Root #{idx + 1}
                        </div>
                        <ReferralNode node={r} depth={0} />
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="text-muted small mt-2">
                Tip: Click + / − to expand/collapse any user's downline.
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default AdminReferralTree;
